/**
 * Performance wallboard — display-only page for on-site screens.
 *
 * Full-bleed, no nav chrome, no interaction. Capability-URL gated:
 * the `?key=<token>` query param is the credential, validated
 * server-side on every refresh.
 *
 * Layout (Option C, ticker variant from the journey iteration): a
 * persistent hero (always-visible "are we on track?" +
 * invoiced-vs-target) sits above a slow continuous horizontal
 * ticker of uniform standardised cards — run-rate, pipeline, and
 * radar figures all in one calm flow. No discrete panel transitions,
 * no chart. See docs/features/performance-wallboard/01_product_brief.md.
 *
 * States designed for: loading, populated (on-track / ahead /
 * behind), stale, error/last-known-good, not-authorised.
 */

import type { JSX, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  WallboardSnapshot,
  WallboardStatus,
} from "@shared-core/data/wallboard-types";
import {
  classifyFreshness,
  formatMoney,
  formatPercent,
  formatTimeOfDay,
  useNowTicker,
} from "@shared-core/domain/wallboard";

/** Auto-refresh cadence. Snapshot is per-month; minutes is plenty. */
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
/**
 * Ticker cycle duration. Picked so a card is visible for roughly six
 * seconds — readable at distance but not lingering. The two
 * duplicated card lists make this an apparent right-to-left scroll
 * that loops seamlessly.
 */
const TICKER_DURATION_S = 60;

export function WallboardPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("key") ?? "";
  const adapter = useDataAdapter();

  const [snapshot, setSnapshot] = useState<WallboardSnapshot | null>(null);
  const [phase, setPhase] = useState<
    "loading" | "ready" | "unauthorised" | "errored"
  >("loading");
  const lastGoodRef = useRef<WallboardSnapshot | null>(null);

  const fetchSnapshot = useCallback(async () => {
    if (!token) {
      setPhase("unauthorised");
      return;
    }
    try {
      const result = await adapter.getWallboardSnapshot(token);
      setSnapshot(result);
      lastGoodRef.current = result;
      setPhase("ready");
    } catch {
      // Degrade to last-known-good rather than blanking the screen.
      // If there is no last-known-good (first ever fetch failed),
      // fall through to the "errored, no snapshot" state below — the
      // page renders the "wallboard temporarily unavailable" screen,
      // and the refresh interval keeps trying.
      if (lastGoodRef.current) {
        setSnapshot(lastGoodRef.current);
      }
      setPhase("errored");
    }
  }, [adapter, token]);

  // Initial fetch + interval refresh.
  useEffect(() => {
    void fetchSnapshot();
    const id = setInterval(() => void fetchSnapshot(), REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchSnapshot]);

  if (phase === "loading") return <LoadingState />;
  if (phase === "unauthorised") return <NotAuthorisedState />;
  if (!snapshot) return <ErrorNoLastGoodState />;

  return (
    <PopulatedWallboard snapshot={snapshot} degraded={phase === "errored"} />
  );
}

// ---------------------------------------------------------------- States ---

function LoadingState(): JSX.Element {
  return (
    <FullBleed>
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-3xl">
        Loading…
      </div>
    </FullBleed>
  );
}

function NotAuthorisedState(): JSX.Element {
  return (
    <FullBleed>
      <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-4">
        <div className="text-5xl">Screen not authorised</div>
        <div className="text-xl opacity-70">
          This wallboard URL is missing or no longer valid.
        </div>
      </div>
    </FullBleed>
  );
}

function ErrorNoLastGoodState(): JSX.Element {
  return (
    <FullBleed>
      <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-4">
        <div className="text-4xl">Wallboard temporarily unavailable</div>
        <div className="text-lg opacity-70">Retrying…</div>
      </div>
    </FullBleed>
  );
}

// ----------------------------------------------------- Populated wallboard ---

function PopulatedWallboard({
  snapshot,
  degraded,
}: {
  snapshot: WallboardSnapshot;
  degraded: boolean;
}): JSX.Element {
  const now = useNowTicker();
  const freshness = classifyFreshness(snapshot.as_at, now);

  return (
    <FullBleed>
      <PageHeader snapshot={snapshot} />
      <Hero snapshot={snapshot} />
      <Ticker snapshot={snapshot} />
      <PageFooter
        asAt={snapshot.as_at}
        freshness={freshness}
        degraded={degraded}
      />
    </FullBleed>
  );
}

function PageHeader({
  snapshot,
}: {
  snapshot: WallboardSnapshot;
}): JSX.Element {
  return (
    <header className="flex items-baseline justify-between px-12 pt-8 pb-4 text-2xl">
      <div className="font-semibold tracking-wide">POLLARDS</div>
      <div className="flex items-baseline gap-6 opacity-80">
        <span>{snapshot.period.label}</span>
        <span className="text-base uppercase tracking-widest opacity-70">
          Day {snapshot.period.days_elapsed} of {snapshot.period.working_days}
        </span>
      </div>
    </header>
  );
}

function Hero({ snapshot }: { snapshot: WallboardSnapshot }): JSX.Element {
  const { monetary, status } = snapshot;
  const pct =
    monetary.monthly_target > 0
      ? monetary.invoiced_mtd / monetary.monthly_target
      : 0;

  return (
    <section className="px-12 pb-6 grid grid-cols-2 gap-12 items-stretch">
      <div className="flex flex-col gap-4">
        <div className="text-2xl uppercase tracking-widest opacity-70">
          Invoiced MTD
        </div>
        <div className="flex items-baseline gap-4">
          <span className="text-7xl font-bold tabular-nums">
            {formatMoney(monetary.invoiced_mtd, { precise: true })}
          </span>
          <span className="text-3xl opacity-60">
            of {formatMoney(monetary.monthly_target, { precise: true })}
          </span>
        </div>
        <ProgressBar fraction={pct} status={status} />
        <div className="text-3xl opacity-80 tabular-nums">
          {formatPercent(pct)} of target
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="text-2xl uppercase tracking-widest opacity-70">
          Gap to Target
        </div>
        <div className="flex items-baseline gap-4">
          <span className="text-7xl font-bold tabular-nums">
            {formatMoney(monetary.gap_to_target, { precise: true })}
          </span>
        </div>
        <StatusPill status={status} />
      </div>
    </section>
  );
}

function ProgressBar({
  fraction,
  status,
}: {
  fraction: number;
  status: WallboardStatus;
}): JSX.Element {
  const pct = Math.max(0, Math.min(1, fraction)) * 100;
  const fill =
    status === "behind"
      ? "bg-[color:var(--color-destructive,#dc2626)]"
      : status === "ahead"
        ? "bg-[color:var(--color-chart-2,#16a34a)]"
        : "bg-[color:var(--color-primary,#2563eb)]";
  return (
    <div
      className="w-full h-5 rounded-full bg-muted overflow-hidden"
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={`h-full ${fill} transition-[width] duration-700`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

const STATUS_PRESENTATION: Record<
  WallboardStatus,
  { label: string; tone: string; glyph: string }
> = {
  on_track: {
    label: "ON TRACK",
    tone: "bg-[color:var(--color-chart-2,#16a34a)]/15 text-[color:var(--color-chart-2,#16a34a)]",
    glyph: "●",
  },
  ahead: {
    label: "AHEAD",
    tone: "bg-[color:var(--color-chart-2,#16a34a)]/15 text-[color:var(--color-chart-2,#16a34a)]",
    glyph: "▲",
  },
  behind: {
    label: "BEHIND",
    tone: "bg-[color:var(--color-destructive,#dc2626)]/15 text-[color:var(--color-destructive,#dc2626)]",
    glyph: "▼",
  },
};

function StatusPill({ status }: { status: WallboardStatus }): JSX.Element {
  const { label, tone, glyph } = STATUS_PRESENTATION[status];
  return (
    <div
      className={`inline-flex items-center gap-3 self-start px-5 py-3 rounded-full text-2xl font-semibold ${tone}`}
    >
      <span aria-hidden>{glyph}</span>
      <span>{label}</span>
    </div>
  );
}

// ----------------------------------------------------------------- Ticker ---

type TickerTone = "neutral" | "warn";

interface TickerCardSpec {
  /** Stable key for keyed iteration. */
  key: string;
  label: string;
  value: string;
  sub?: string;
  tone: TickerTone;
}

/**
 * Standardised uniform-shape cards for the slow ticker.
 *
 * Order chosen to flow naturally from "what's happening right now"
 * (today's bookings, the run-rate verdict) through pipeline value to
 * operational counts. The ticker loops, so first/last placement is
 * not load-bearing — but keeping related cards adjacent reduces
 * cognitive load as the eye tracks one card at a time.
 */
function buildTickerCards(snapshot: WallboardSnapshot): TickerCardSpec[] {
  const { daily, monetary, radar } = snapshot;
  return [
    {
      key: "booked-today",
      label: "Booked Today",
      value: formatMoney(daily.booked_today),
      sub: `vs daily target ${formatMoney(daily.target_daily)}`,
      tone: "neutral",
    },
    {
      key: "expected-today",
      label: "Expected by Today",
      value: formatMoney(daily.expected_by_today),
      sub: `Current avg ${formatMoney(daily.current_daily_average)}`,
      tone: "neutral",
    },
    {
      key: "needed-remaining",
      label: "Needed Per Day",
      value: formatMoney(daily.daily_needed_remaining),
      sub: "to hit monthly target",
      tone: "neutral",
    },
    {
      key: "wip",
      label: "WIP",
      value: formatMoney(monetary.wip),
      sub: "ordered, not delivered",
      tone: "neutral",
    },
    {
      key: "dni",
      label: "Delivered Not Invoiced",
      value: formatMoney(monetary.delivered_not_invoiced),
      sub: "jobs delivered, not yet billed",
      tone: "neutral",
    },
    {
      key: "past-due",
      label: "Past Due Date",
      value: `${radar.past_due_count}`,
      sub: radar.past_due_count > 0 ? "jobs need attention" : "all clear",
      tone: radar.past_due_count > 0 ? "warn" : "neutral",
    },
    {
      key: "awaiting-invoice",
      label: "Jobs Awaiting Invoice",
      value: `${radar.awaiting_invoice_count}`,
      sub: "delivered, not yet billed",
      tone: radar.awaiting_invoice_count > 10 ? "warn" : "neutral",
    },
  ];
}

function Ticker({ snapshot }: { snapshot: WallboardSnapshot }): JSX.Element {
  const cards = buildTickerCards(snapshot);
  // Duplicate so when the first set scrolls off, the second is
  // already in place — the keyframes loop from translateX(0) to
  // translateX(-50%) for a seamless join.
  const duplicated = [...cards, ...cards];

  return (
    <section
      aria-label="Live performance figures"
      className="flex-1 flex items-center border-t border-border overflow-hidden"
    >
      <div
        className="flex gap-6 px-12 will-change-transform"
        style={{
          animation: `wallboard-ticker ${TICKER_DURATION_S}s linear infinite`,
          width: "max-content",
        }}
      >
        {duplicated.map((card, i) => (
          <TickerCard
            // Index makes the duplicated set unique without changing key semantics
            key={`${card.key}-${i}`}
            spec={card}
          />
        ))}
      </div>
    </section>
  );
}

function TickerCard({ spec }: { spec: TickerCardSpec }): JSX.Element {
  const toneClass =
    spec.tone === "warn"
      ? "border-[color:var(--color-destructive,#dc2626)]/40"
      : "border-border";
  return (
    <article
      className={`w-80 shrink-0 rounded-2xl border-2 ${toneClass} bg-card/50 p-8 flex flex-col gap-3`}
    >
      <div className="text-base uppercase tracking-widest opacity-60">
        {spec.label}
      </div>
      <div className="text-6xl font-bold tabular-nums leading-none">
        {spec.value}
      </div>
      {spec.sub && (
        <div className="text-base opacity-70 mt-auto">{spec.sub}</div>
      )}
    </article>
  );
}

// Keyframes for the ticker animation live in
// `apps/frontend/src/styles/wallboard.css` (imported via index.css),
// not inline. An inline `<style>` tag here would be blocked under a
// strict CSP without a nonce, and the rest of the app's stylesheet
// pipeline handles cache-busting / bundling for us.

// ----------------------------------------------------------------- Footer ---

function PageFooter({
  asAt,
  freshness,
  degraded,
}: {
  asAt: string;
  freshness: "fresh" | "stale" | "ancient";
  degraded: boolean;
}): JSX.Element {
  const freshnessLabel =
    freshness === "fresh"
      ? `Updated ${formatTimeOfDay(asAt)}`
      : freshness === "stale"
        ? `Stale — last updated ${formatTimeOfDay(asAt)}`
        : `Last-known-good — ${formatTimeOfDay(asAt)}`;
  const toneClass =
    freshness === "fresh"
      ? "opacity-60"
      : "opacity-90 text-[color:var(--color-destructive,#dc2626)]";

  return (
    <footer className="flex items-center justify-end px-12 py-6 text-base">
      <div className={toneClass}>
        {freshnessLabel}
        {degraded && " · using last known data"}
      </div>
    </footer>
  );
}

// ------------------------------------------------------------------ Shell ---

function FullBleed({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="min-h-screen w-full flex flex-col bg-background text-foreground select-none">
      {children}
    </div>
  );
}
